import React, {Fragment, useMemo} from 'react';
import {Button, Dimmer, Popup, Segment, Table} from "semantic-ui-react";
import Loader from "../../../components/Loader/Loader";
import TableStyled from "../../../components/Table/Table";
import numeral from "numeral";
import {Trans} from "react-i18next";
import Logos from "./logos";
import Info from "../../../components/Info";
import {customMatchingServices, declOfNum, formServicesArray} from "./utils";
import i18n from "../../../services/i18n";
import {costFormat} from "../../../services/cost";
import {getCarrierUiExtension} from "./carrierExtensions";

/**
 * Режимы ПВЗ СДЭК (коды справочника deliveryType):
 * 4 — ПВЗ→дверь (забор в ПВЗ, доставка курьером до адреса)
 * 5 — дверь→ПВЗ (забор курьером с адреса, вручение в ПВЗ)
 * 6 — ПВЗ→ПВЗ
 * Иконки в гриде — только визуальная подсказка к выбранному условию доставки (title), без отдельного действия по клику.
 */
const getPvzRouteKinds = (deliveryTypeCode) => {
    const c = deliveryTypeCode != null ? String(deliveryTypeCode) : '';
    switch (c) {
        case '4':
            return { pickup: 'pvz', delivery: 'courier' };
        case '5':
            return { pickup: 'courier', delivery: 'pvz' };
        case '6':
            return { pickup: 'pvz', delivery: 'pvz' };
        default:
            return { pickup: null, delivery: null };
    }
};

const RouteModeIcon = ({ kind }) => {
    if (!kind) return null;

    const isPvz = kind === 'pvz';
    const commonProps = {
        className: `tariff-route-col__icon ${isPvz ? 'tariff-route-col__icon_pvz' : 'tariff-route-col__icon_courier'}`,
        width: 40,
        height: 40,
        viewBox: '0 0 16 16',
        fill: 'none',
        xmlns: 'http://www.w3.org/2000/svg',
        role: 'img',
        'aria-label': isPvz ? 'ПВЗ' : 'Дверь',
        title: isPvz ? 'ПВЗ' : 'Дверь'
    };

    return (
        <div className="tariff-route-container">
            {isPvz ? (
                <svg {...commonProps}>
                    <path d="M3 14H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    <path d="M4 14V4.5C4 4.22386 4.22386 4 4.5 4H11.5C11.7761 4 12 4.22386 12 4.5V14" stroke="currentColor" strokeWidth="1.5"/>
                    <rect x="6" y="6" width="1.5" height="1.5" fill="currentColor"/>
                    <rect x="8.5" y="6" width="1.5" height="1.5" fill="currentColor"/>
                    <rect x="6" y="8.5" width="1.5" height="1.5" fill="currentColor"/>
                    <rect x="8.5" y="8.5" width="1.5" height="1.5" fill="currentColor"/>
                    <path d="M7.5 14V11.5C7.5 11.2239 7.72386 11 8 11V11C8.27614 11 8.5 11.2239 8.5 11.5V14" stroke="currentColor" strokeWidth="1.5"/>
                </svg>
            ) : (
                <svg {...commonProps}>
                    <path d="M2 4.5H9.5V10H2V4.5Z" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M9.5 6H12.2L14 7.9V10H9.5V6Z" stroke="currentColor" strokeWidth="1.5"/>
                    <circle cx="5" cy="11.5" r="1.5" stroke="currentColor" strokeWidth="1.5"/>
                    <circle cx="11.5" cy="11.5" r="1.5" stroke="currentColor" strokeWidth="1.5"/>
                </svg>
            )}
            <span className="tariff-route-label">
                {isPvz ? 'ПВЗ' : 'Дверь'}
            </span>
        </div>
    );
};

const renderDays = (number, currentLanguage) => {
    if (currentLanguage === "ru") {
        return (<span>{number} {declOfNum(number, ['день', 'дня', 'дней'])}</span>);
    } else if (currentLanguage === "en") {
        return (<span>{number} days</span>);
    }
    return null;
}

/** На витрине /calculator скрываем бренд ТК в начале названия тарифа (например «СДЭК Документы…»). */
const stripLeadingCarrierBrand = (title, carrierName) => {
    if (!title) {
        return '';
    }

    const text = String(title).trim();
    const candidates = [];
    if (carrierName) {
        candidates.push(String(carrierName).trim());
    }
    // Тикет: явно убрать «СДЭК» даже если carrierName слегка отличается (ПВЗ-СДЭК и т.п.)
    ['ПВЗ-СДЭК', 'СДЭК', 'CDEK'].forEach((brand) => {
        if (brand && !candidates.some((c) => c.toLowerCase() === brand.toLowerCase())) {
            candidates.push(brand);
        }
    });
    candidates.sort((a, b) => b.length - a.length);

    for (let i = 0; i < candidates.length; i++) {
        const name = candidates[i];
        if (!name) {
            continue;
        }
        const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const re = new RegExp(`^${escaped}(?:\\s*[-–—:]\\s*|\\s+)`, 'i');
        if (re.test(text)) {
            const stripped = text.replace(re, '').trim();
            if (stripped) {
                return stripped;
            }
        }
    }

    return text;
};

const renderTariff = (collection, entry, ind, currentLanguage = 'ru', disabled, form, changeParam, setStep, isCalc, isCalcV2, isTanderCalc, isCostCalc, onClickSendBtn, isView, showPvzRouteColumns) => {
    const carrierExtension = getCarrierUiExtension(entry);

    const hasHouseNumber = (address) => {
        if (!address) return false;
        return !!(address.house && address.house.trim()) || !!(address.building && address.building.trim());
    };

    const handleSelectTariff = (collection, index, deliveryDays) => {
        // Блокируем переход для Яндекса и Достависты при неполных адресах
        const selectedTariff = collection[index];
        const tariffCarrierCode = selectedTariff.carrierCode || (selectedTariff.displayData && selectedTariff.displayData.carrierCode);
        const isTariffYandexOrDostavista = tariffCarrierCode === 'Ya' || tariffCarrierCode === 'Dostavista';
        if (isTariffYandexOrDostavista && form && (!hasHouseNumber(form.shipmentAddress) || !hasHouseNumber(form.deliveryAddress))) {
            return; // Не выполняем переход
        }
        
        let selectedTariffFinal = collection[index];
        setStep(4);
        changeParam('selectedTariff', selectedTariffFinal);
        changeParam('carrierPublicOfferUrl', selectedTariffFinal.carrierPublicOfferUrl);
        changeParam('deliveryDay', deliveryDays);
        carrierExtension.onTariffSelected({
            selectedTariff: selectedTariffFinal,
            changeParam
        });
    }

    // Проверяем, нужно ли блокировать кнопку для Яндекса и Достависты при неполных адресах
    const carrierCode = entry.carrierCode || (entry.displayData && entry.displayData.carrierCode);
    const isYandexOrDostavista = carrierCode === 'Ya' || carrierCode === 'Dostavista';
    const isAddressIncomplete = form && (!hasHouseNumber(form.shipmentAddress) || !hasHouseNumber(form.deliveryAddress));
    const shouldBlockTariff = isYandexOrDostavista && isAddressIncomplete;
    const isTariffDisabled = disabled;

    const renderEntryStatus = (entryStatus, statusComment) => {
        let text = null;
        let color = null;

        switch (entryStatus) {
            case 'Sent': {
                text = 'Заявка отправлена';
                color = 'yellow';
                break;
            }
            case 'Error': {
                text = 'Ошибка';
                color = 'red';
                break;
            }
            case 'Complete': {
                text = 'Заявка обработана';
                color = 'green';
                break;
            }
            case 'Skip': {
                text = 'Без интеграции';
                color = 'blue';
                break;
            }
            default:
                break;
        }

        return (text && color) ? <div className={`cost-calc-status ${color}`} title={statusComment}>{text}</div> : null;
    }

    let displayData = entry.displayData || {};
    const carrierName = displayData.carrierName || entry.carrierName;
    const shortTitleRaw = carrierExtension.getShortTariffTitle(entry);
    const shortTitle = isTanderCalc
        ? stripLeadingCarrierBrand(shortTitleRaw, carrierName)
        : shortTitleRaw;
    let entryCost = displayData.cost ? numeral(displayData.cost).format("0.00") : '';
    let deliveryDays;
    if (displayData.maxDeliveryDay !== undefined && displayData.maxDeliveryDay !== "") {
        deliveryDays =
            <div>
                <Trans>{displayData.minDeliveryDay}</Trans><span> - </span><Trans>{displayData.maxDeliveryDay}</Trans>
            </div>;
    } else {
        deliveryDays = <div><Trans>{displayData.minDeliveryDay}</Trans></div>;
    }

    const calcCost = (entryCost = 0) => {
        //расчет стоимости с учетом выбранных услуг
        let cost = Number.parseFloat(entryCost);
        formServicesArray(form).forEach(fs => {
            const availableServices = entry.services || []
            const service = availableServices.find(s => s.code === fs || customMatchingServices(fs, s.code, entry.carrierCode)) || {};
            cost += service.cost || 0;
        })
        return cost.toFixed(2);
    }

    const pvzDt = form && form.deliveryType != null ? String(form.deliveryType) : '';
    const routeKinds = showPvzRouteColumns ? getPvzRouteKinds(pvzDt) : { pickup: null, delivery: null };
    const hideRouteDates = showPvzRouteColumns && (pvzDt === '4' || pvzDt === '6');
    const showPvzRouteIcons = showPvzRouteColumns && ['4', '5', '6'].includes(pvzDt);

    const renderTariffDescription = (className = 'carrier-tariff-inline carrier-tariff-inline_result') => (
        shortTitle ? (
            <div className={className}>
                {!isTanderCalc && (
                    <Info
                        className='carrier-tariff-inline__icon'
                        name={carrierName}
                        label={displayData.carrierServiceName || entry.carrierServiceName}
                        text={displayData.carrierServiceDescription || entry.carrierServiceDescription}
                        position={'left'}
                        offset={[-10]}
                    />
                )}
                <div className='carrier-tariff-inline__title'>{shortTitle}</div>
            </div>
        ) : null
    );

    return (<Table.Row className={entry.isSelected ? 'row_green' : ''} key={'tariff_' + ind}>
        {!isTanderCalc && <Table.Cell>
            <Logos
                className={(isCalc && !isCalcV2) ? 'blur' : undefined}
                name={carrierCode}
                carrierTitle={displayData.carrierName || entry.carrierName}
            />
        </Table.Cell>}
        {!isCalc && <Table.Cell colSpan='2' className='table__info'>
            {renderTariffDescription()}
        </Table.Cell>}
        <Table.Cell>
            {showPvzRouteIcons && hideRouteDates ? (
                <div className='tariff-route-col'>
                    <RouteModeIcon kind={routeKinds.pickup} />
                </div>
            ) : showPvzRouteIcons && !hideRouteDates ? (
                <Fragment>
                    <div className='tariff-route-col'>
                        <RouteModeIcon kind={routeKinds.pickup} />
                    </div>
                    <div className='table__date'>{displayData.departureDate}</div>
                    <div className='table__week-day'><Trans>{displayData.departureDay}</Trans></div>
                </Fragment>
            ) : (
                <Fragment>
                    {isCalc && renderTariffDescription('carrier-tariff-inline carrier-tariff-inline_result carrier-tariff-inline_calc')}
                    <div className='table__date'>{displayData.departureDate}</div>
                    <div className='table__week-day'><Trans>{displayData.departureDay}</Trans></div>
                </Fragment>
            )}
        </Table.Cell>
        {
            (!(isCostCalc && isNaN(calcCost(entryCost))) && !displayData.maxDeliveryDay && !displayData.minDeliveryDay && !displayData.deliveryTime) ?
                <Table.Cell colSpan={2} style={{paddingLeft: '5%'}}>
                    Уточните у менеджера
                </Table.Cell> :
                <Fragment>
                    <Table.Cell textAlign='center'>
                        {displayData.maxDays === 0 ?
                            <div className='table__days-count table__days-count_0'>0 дней</div> :
                            <div className='table__days-count'>{displayData.maxDays ? renderDays(displayData.maxDays, currentLanguage) : ''}</div>}
                    </Table.Cell>
                    <Table.Cell>
                        {showPvzRouteIcons && hideRouteDates ? (
                            <div className='tariff-route-col'>
                                <RouteModeIcon kind={routeKinds.delivery} />
                            </div>
                        ) : showPvzRouteIcons && !hideRouteDates ? (
                            <Fragment>
                                <div className='tariff-route-col'>
                                    <RouteModeIcon kind={routeKinds.delivery} />
                                </div>
                                <div className='table__date'>{displayData.deliveryTime}</div>
                                <div className='table__week-day'>{deliveryDays}</div>
                            </Fragment>
                        ) : (
                            <Fragment>
                                <div className='table__date'>{displayData.deliveryTime}</div>
                                <div className='table__week-day'>{deliveryDays}</div>
                            </Fragment>
                        )}
                    </Table.Cell>
                </Fragment>
        }

        <Table.Cell style={{minWidth: isCostCalc && '175px'}} textAlign='center'>
            {!isNaN(calcCost(entryCost)) && (isCalc || (isCostCalc && isView)  ? <div className='table__cost-calc'>{costFormat(calcCost(entryCost))} ₽</div>
                : (!isCostCalc ? (
                    shouldBlockTariff ? (
                        <Popup
                            trigger={
                                <div style={{display: 'inline-block'}}>
                                    <Button
                                        disabled={isTariffDisabled}
                                        onClick={() => handleSelectTariff(collection, ind, displayData.maxDeliveryDay)}
                                        className='table__btn'
                                    >
                                        {displayData.isCostHidden ? (
                                            <Info
                                                name=""
                                                label=""
                                                text={displayData.costHiddenMessage || 'Для определения стоимости отправки необходимо указать полные адреса до номера дома для точки отправления и точки доставки'}
                                                position={'top center'}
                                                offset={[0]}
                                            />
                                        ) : `${costFormat(calcCost(entryCost))} ₽`}
                                    </Button>
                                </div>
                            }
                            content='Для определения стоимости отправки необходимо указать полные адреса до номера дома для точки отправления и точки доставки'
                            position='top center'
                            on='hover'
                            mouseEnterDelay={0}
                            mouseLeaveDelay={0}
                        />
                    ) : (
                        <Button
                            disabled={isTariffDisabled}
                            onClick={() => handleSelectTariff(collection, ind, displayData.maxDeliveryDay)}
                            className='table__btn'
                        >
                            {displayData.isCostHidden ? (
                                <Info
                                    name=""
                                    label=""
                                    text={displayData.costHiddenMessage || 'Для определения стоимости отправки необходимо указать полные адреса до номера дома для точки отправления и точки доставки'}
                                    position={'top center'}
                                    offset={[0]}
                                />
                            ) : `${costFormat(calcCost(entryCost))} ₽`}
                        </Button>
                    )
                ) : (
                    shouldBlockTariff ? (
                        <Popup
                            trigger={
                                <div style={{display: 'inline-block'}}>
                                    <Button
                                        disabled={isTariffDisabled}
                                        onClick={() => {
                                            // Блокируем открытие модального окна для Яндекса и Достависты при неполных адресах
                                            if (!shouldBlockTariff) {
                                                onClickSendBtn(entry);
                                            }
                                        }}
                                        className='table__btn step-btn-block__btn_final table__btn cost-calc_btn'
                                        color='blue'
                                        text={`${costFormat(calcCost(entryCost))} ₽`}
                                    >
                                        <span>
                                            Выбрать <br/> вариант доставки
                                        </span>
                                    </Button>
                                </div>
                            }
                            content='Для определения стоимости отправки необходимо указать полные адреса до номера дома для точки отправления и точки доставки'
                            position='top center'
                            on='hover'
                            mouseEnterDelay={0}
                            mouseLeaveDelay={0}
                        />
                    ) : (
                        <Button
                            disabled={isTariffDisabled}
                            onClick={() => onClickSendBtn(entry)}
                            className='table__btn step-btn-block__btn_final table__btn cost-calc_btn'
                            color='blue'
                            text={`${costFormat(calcCost(entryCost))} ₽`}
                        >
                            <span>
                                Выбрать <br/> вариант доставки
                            </span>
                        </Button>
                    )
                )))}
        </Table.Cell>
        {isCalc && <Table.Cell textAlign='center'>
            <Button onClick={() => {
                if (isTanderCalc) {
                    window.open('https://auth.artlogic-sk.ru/Identity/Account/ExpressLogin#register', "_self");
                } else {
                    window.open('https://pooling.me/contactwithus', "_self");
                }
            }}
                    className='table__btn table__btn_big'>
                Заказать
            </Button>
        </Table.Cell>}
        {isCostCalc && <Table.Cell>
            {renderEntryStatus(entry.status, entry.statusComment)}
        </Table.Cell>}
    </Table.Row>)
}

const ResultData = ({tariffs, loading, form, changeParam, setStep, isCalc = false, isCalcV2 = false, isCostCalc = false, isTanderCalc = false, onClickSendBtn, isView}) => {
    i18n.changeLanguage('ru');

    const pvzDeliveryTypeCodes = ['4', '5', '6'];
    const pvzCarrierName = 'ПВЗ-СДЭК';
    const selectedDeliveryTypeCode = form && form.deliveryType != null ? String(form.deliveryType) : null;
    const isPvzDeliveryType = pvzDeliveryTypeCodes.includes(selectedDeliveryTypeCode);
    const showPvzRouteColumns = !isCalc && isPvzDeliveryType;
    const useRouteOnlyColumns = showPvzRouteColumns && (selectedDeliveryTypeCode === '4' || selectedDeliveryTypeCode === '6');
    const filteredTariffsRaw = isPvzDeliveryType
        ? (tariffs || []).filter(t =>
            (t && (t.carrierName || (t.displayData && t.displayData.carrierName))) === pvzCarrierName
            && pvzDeliveryTypeCodes.includes(String((t.displayData && t.displayData.deliveryTypeCode) || t.deliveryTypeCode || ''))
        )
        : tariffs;
    const filteredTariffs = useMemo(() => {
        const src = filteredTariffsRaw || [];
        const seen = new Set();
        const result = [];
        const toKeyPart = (value) => value == null ? '' : value;

        src.forEach((t, idx) => {
            if (!t) return;
            const d = t.displayData || {};
            const signature = [
                toKeyPart(t.carrierId),
                toKeyPart(t.carrierServiceCode),
                toKeyPart(t.carrierServiceName),
                toKeyPart(t.vehicleTypeId),
                toKeyPart(d.deliveryMode != null ? d.deliveryMode : t.deliveryMode),
                toKeyPart(d.deliveryTypeCode != null ? d.deliveryTypeCode : t.deliveryTypeCode),
                toKeyPart(d.cost != null ? d.cost : t.cost),
                toKeyPart(d.departureDate != null ? d.departureDate : t.departureDate),
                toKeyPart(d.deliveryTime),
                toKeyPart(d.minDeliveryDay),
                toKeyPart(d.maxDeliveryDay),
                toKeyPart(t.status)
            ].join('|');
            if (seen.has(signature)) return;
            seen.add(signature);
            result.push(t);
        });

        return result;
    }, [filteredTariffsRaw]);

    return <Segment className='b-b-0' vertical>
        <Dimmer inverted active={loading} className={'result-dimmer'}>
            <Loader label={'Загрузка предложений'} description={'Поиск тарифов в системе'}/>
        </Dimmer>
        <TableStyled>
            <Table.Header>
                <Table.Row>
                    {!isTanderCalc && <Table.HeaderCell>Перевозчик</Table.HeaderCell>}
                    {!isCalc && <Table.HeaderCell colSpan='2'/>}
                    <Table.HeaderCell>{useRouteOnlyColumns ? 'Забор груза' : 'Плановая дата отгрузки'}</Table.HeaderCell>
                    <Table.HeaderCell textAlign='center'>В пути</Table.HeaderCell>
                    <Table.HeaderCell>{useRouteOnlyColumns ? 'Доставка груза' : `${!isCostCalc ? 'Ожидаемая дата' : 'Дата'} доставки`}</Table.HeaderCell>
                    <Table.HeaderCell textAlign='center'>{!isCostCalc ? 'Тариф' : 'Стоимость'} (с НДС)</Table.HeaderCell>
                    {isCalc && <Table.HeaderCell textAlign='center'/>}
                    {isCostCalc && <Table.HeaderCell>Статус</Table.HeaderCell>}
                </Table.Row>
            </Table.Header>
            <Table.Body>
                {
                    filteredTariffs && filteredTariffs.map((tariff, index) => renderTariff(filteredTariffs, tariff, index, 'ru', loading, form, changeParam, setStep, isCalc, isCalcV2, isTanderCalc, isCostCalc, onClickSendBtn, isView, showPvzRouteColumns))
                }
                {
                    (!filteredTariffs || filteredTariffs.length === 0) && !loading && <Table.Row className='disabled-tr'>
                        <Table.Cell textAlign='center' colSpan={!isCostCalc ? 7 : 8}>
                            Варианты доставки не найдены
                        </Table.Cell>
                    </Table.Row>
                }
            </Table.Body>
        </TableStyled>
    </Segment>
}

export default ResultData;


// WEBPACK FOOTER //
// ./App/pages/parcels/form_v3/resultData.jsx